'use client'

import { AuthCard } from '@/components/Auth/AuthCard'
import { AuthFormField } from '@/components/Auth/AuthFormField'
import { LoadingButton } from '@/components/ui/loading-button'
import { useAuth } from '@/providers/Auth'
import { zodResolver } from '@hookform/resolvers/zod'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import React from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required.').email('Please enter a valid email address.'),
  password: z.string().min(1, 'Please provide a password.'),
})

type LoginFormData = z.infer<typeof loginSchema>

export const LoginForm: React.FC = () => {
  const searchParams = useSearchParams()
  const redirectTo = searchParams.get('redirect')

  const { login } = useAuth()
  const router = useRouter()

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    const result = (await login(data)) as { error?: string } | undefined

    if (result?.error) {
      setError('root', { message: result.error })
      return
    }

    if (redirectTo) {
      router.push(redirectTo)
    } else {
      router.push('/tms')
    }
  }

  return (
    <AuthCard
      title="Log in"
      subtitle="Sign in to your Lynx Permits account."
      showParamAlerts
      footer={
        <>
          <p>
            Don&apos;t have an account?{' '}
            <Link
              href="/signup"
              className="font-medium text-brand-black underline underline-offset-2 hover:opacity-70 transition-opacity"
            >
              Sign up
            </Link>
          </p>
          <p className="mt-1">
            <Link
              href="/forgot-password"
              className="font-medium text-brand-black underline underline-offset-2 hover:opacity-70 transition-opacity"
            >
              Forgot your password?
            </Link>
          </p>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        {/* Server / root error */}
        {errors.root && (
          <div
            role="alert"
            aria-live="assertive"
            className="rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-destructive"
          >
            {errors.root.message}
          </div>
        )}

        <div className="space-y-3">
          <div className="mb-3 last:mb-0">
            <AuthFormField
              id="login-email"
              label="Email"
              type="email"
              error={errors.email?.message}
              placeholder="you@example.com"
              autoComplete="username"
              required
              disabled={isSubmitting}
              {...register('email')}
            />
          </div>
          <div className="mb-3 last:mb-0">
            <AuthFormField
              id="login-password"
              label="Password"
              type="password"
              error={errors.password?.message}
              placeholder="••••••••"
              autoComplete="current-password"
              required
              disabled={isSubmitting}
              {...register('password')}
            />
          </div>
        </div>

        <LoadingButton
          type="submit"
          loading={isSubmitting}
          loadingText="Signing in…"
          className="w-full tracking-tight mt-2"
        >
          Login
        </LoadingButton>
      </form>
    </AuthCard>
  )
}
